import { useEffect, useState, useRef, useCallback } from 'react';
import { getIntakeProcessingStatusIntakeIntakeIdProcessingStatusGet } from '../services/api/sdk.gen';
import type { IntakeProcessingStatusResponse } from '../services/api/types.gen';

interface UseWorkflowStatusOptions {
  intakeId: string | null;
  enabled?: boolean;
  pollingInterval?: number; // in milliseconds
}

interface WorkflowStatus {
  stage: string | null;
  progress_percentage: number;
  current_activity: string | null;
  error_message: string | null;
  started_at: string | null;
  elapsed_seconds: number;
  workflow_found: boolean;
  isLoading: boolean;
  isFailed: boolean;
  isCompleted: boolean;
}

/**
 * Hook to poll workflow processing status from Temporal
 *
 * Polls the /intake/{id}/processing-status endpoint to get real-time
 * workflow progress. Automatically stops polling when workflow is
 * completed or failed.
 */
export const useWorkflowStatus = ({
  intakeId,
  enabled = true,
  pollingInterval = 5000, // Default: 5 seconds
}: UseWorkflowStatusOptions): WorkflowStatus => {
  const [status, setStatus] = useState<WorkflowStatus>({
    stage: null,
    progress_percentage: 0,
    current_activity: null,
    error_message: null,
    started_at: null,
    elapsed_seconds: 0,
    workflow_found: false,
    isLoading: true,
    isFailed: false,
    isCompleted: false,
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const isPollingRef = useRef(false);

  const scheduleNextPoll = useCallback((delay: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      fetchStatus();
    }, delay);
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!intakeId || !enabled) {
      return;
    }

    // Prevent overlapping requests
    if (isPollingRef.current) {
      return;
    }

    isPollingRef.current = true;

    try {
      const response =
        await getIntakeProcessingStatusIntakeIntakeIdProcessingStatusGet({
          path: { intake_id: intakeId },
        });

      if (!mountedRef.current) {
        isPollingRef.current = false;
        return;
      }

      if (response.data) {
        const data = response.data as IntakeProcessingStatusResponse;
        const isFailed = data.stage === 'failed' || !!data.error_message;
        const isCompleted =
          data.stage === 'completed' || data.progress_percentage === 100;

        setStatus({
          stage: data.stage || null,
          progress_percentage: data.progress_percentage || 0,
          current_activity: data.current_activity || null,
          error_message: data.error_message || null,
          started_at: data.started_at || null,
          elapsed_seconds: data.elapsed_seconds || 0,
          workflow_found: data.workflow_found || false,
          isLoading: false,
          isFailed,
          isCompleted,
        });

        // Continue polling if not completed or failed
        if (!isCompleted && !isFailed) {
          scheduleNextPoll(pollingInterval);
        }
      } else {
        // No data, try again
        scheduleNextPoll(pollingInterval);
      }
    } catch (error) {
      if (mountedRef.current) {
        console.error('Error fetching workflow status:', error);
        setStatus(prev => ({
          ...prev,
          isLoading: false,
        }));
        // Retry on error
        scheduleNextPoll(pollingInterval);
      }
    } finally {
      isPollingRef.current = false;
    }
  }, [intakeId, enabled, pollingInterval, scheduleNextPoll]);

  useEffect(() => {
    mountedRef.current = true;

    if (!intakeId || !enabled) {
      return;
    }

    // Start polling with immediate first fetch
    fetchStatus();

    // Cleanup
    return () => {
      mountedRef.current = false;
      isPollingRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [intakeId, enabled, fetchStatus]);

  return status;
};
